import {useCallback, useEffect, useRef, useState} from 'react';
import {calculateTicketCost, fetchTicket} from '../_utilities/ticketUtils';
import {search} from '../_utilities/reactUtils';
import {normalizeTicket, saveTicketField} from '../_utilities/ticketUpdateService';
import useInterval from './useInterval';

export default function useTicket(ticketId, paused) {
    const [ticket, setTicket] = useState(null);
    const [agentList, setAgentList] = useState([]);
    const [ticketCost, setTicketCost] = useState('$0');
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const current = useRef({ticketId, paused, mounted: false, generation: 0, reading: null, saving: false});
    // Invalidate requests immediately when navigation or editing changes their context.
    if (current.current.ticketId !== ticketId || current.current.paused !== paused) {
        current.current.generation++;
        current.current.ticketId = ticketId;
        current.current.paused = paused;
    }
    const isCurrent = generation => current.current.mounted && current.current.generation === generation;

    useEffect(() => {
        const state = current.current;
        state.mounted = true;
        setTicket(null);
        setTicketCost('$0');
        setError(null);
        setLoadError(null);
        setSaving(false);
        state.saving = false;
        return () => {
            state.mounted = false;
            state.generation++;
        };
    }, [ticketId]);

    useEffect(() => {
        let active = true;
        search('contact', ['group_id IN (1,2,3,4)'])
            .then(agents => { if (active) setAgentList(agents || []); })
            .catch(err => { if (active) setLoadError(err.message || 'Unable to load agents.'); });
        return () => { active = false; };
    }, []);

    const refresh = useCallback(async () => {
        const state = current.current;
        if (state.paused || state.saving || state.reading === state.generation) return;
        const generation = state.generation;
        state.reading = generation;
        try {
            const data = normalizeTicket(await fetchTicket(ticketId));
            if (!isCurrent(generation) || current.current.paused) return;
            setTicket(data);
            setLoadError(null);
            const cost = await calculateTicketCost(data);
            if (isCurrent(generation) && !current.current.paused) setTicketCost(cost);
        } catch (err) {
            if (isCurrent(generation)) setLoadError(err.message || 'Unable to load ticket.');
        } finally {
            if (state.reading === generation) state.reading = null;
        }
    }, [ticketId]);

    const refreshCost = async () => {
        if (!ticket) return;
        const generation = current.current.generation;
        try {
            const cost = await calculateTicketCost(ticket);
            if (isCurrent(generation)) setTicketCost(cost);
        } catch (err) {
            if (isCurrent(generation)) setLoadError(err.message || 'Unable to load ticket cost.');
        }
    };

    const update = async (field, value) => {
        if (!ticket || current.current.saving) return null;
        current.current.saving = true;
        current.current.generation++;
        setSaving(true);
        setError(null);
        try {
            const updated = await saveTicketField(ticket, field, value);
            if (!current.current.mounted || current.current.ticketId !== ticketId) return null;
            setTicket(updated);
            return updated;
        } catch (err) {
            if (current.current.mounted && current.current.ticketId === ticketId) {
                setError(err.message || 'Unable to save ticket.');
            }
            return null;
        } finally {
            if (current.current.ticketId === ticketId && current.current.mounted) {
                current.current.saving = false;
                setSaving(false);
            }
        }
    };

    useInterval(refresh, paused || saving ? null : 5000);
    // Fetch immediately when the route changes, even if the interval delay stays the same.
    useEffect(() => { refresh(); }, [refresh, paused]);
    return {ticket, agentList, ticketCost, error: error || loadError, saving, update, refreshCost, setError};
}
